"use client";

/**
 * @file Guide Generator demo UI page.
 *
 * This page demonstrates the guide generator feature by providing a simple UI
 * for selecting guide variants and generating new guides.
 */

import type { Expression } from "fuse.js";
import type { Dispatch, FC, SetStateAction } from "react";

import dedent from "dedent";
import Fuse from "fuse.js";
import { useCallback, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type {
  AudienceRoleListQuery,
  GuideGenerateMutation,
  GuideVariantListQuery,
} from "@/app/guide-generator/_helpers/hooks";

import {
  useAudienceRoleListQuery,
  useGuideGenerateMutation,
  useGuideVariantListQuery,
} from "@/app/guide-generator/_helpers/hooks";
import { SdkProvider, useSdk } from "@/app/guide-generator/_helpers/sdk";
import styles from "@/app/guide-generator/page.module.css";
import {
  AiEffort,
  AiProvider,
  AnthropicModel,
  GoogleModel,
  OpenAiModel,
  providerDefaultModelMap,
} from "@/lib/core/ai/types";
import {
  Fluency,
  fluencyLabelMap,
  GuideLength,
  guideLengthLabelMap,
  Locale,
  localeLabelMap,
} from "@/lib/core/payload/enums";
import { GuideGeneratorInput } from "@/lib/features/guide-generator/types";

/**
 * Build model options for a provider with a default option.
 */
const buildModelOptionList = (
  modelEnumOptionList: readonly string[],
  defaultModel: string,
): { label: string; value: string }[] => [
  { label: `${defaultModel} (default)`, value: `` },
  ...modelEnumOptionList.map((model) => ({ label: model, value: model })),
];

/**
 * Model options by provider.
 */
const modelMap: Record<AiProvider, { label: string; value: string }[]> = {
  anthropic: buildModelOptionList(
    AnthropicModel.options,
    providerDefaultModelMap.anthropic,
  ),
  google: buildModelOptionList(
    GoogleModel.options,
    providerDefaultModelMap.google,
  ),
  openai: buildModelOptionList(
    OpenAiModel.options,
    providerDefaultModelMap.openai,
  ),
};

const Page: FC = () => {
  return (
    <SdkProvider>
      <PageContent />
    </SdkProvider>
  );
};

type GenerateOptionsInputState = Pick<
  GuideGeneratorInput,
  `ai` | `audienceRoleId` | `fluency` | `length` | `locale`
>;

type GuideVariantListInputState = {
  filter: string;
} & Pick<GuideGeneratorInput, `variantIdList`>;

const PageContent: FC = () => {
  const { auth } = useSdk();

  const [guideVariantListInputState, setGuideVariantListInputState] =
    useState<GuideVariantListInputState>({
      filter: ``,
      variantIdList: [],
    });

  const [generateOptionsInputState, setGenerateOptionsInputState] =
    useState<GenerateOptionsInputState>({
      ai: {
        provider: `google`,
      },
      fluency: undefined,
      length: `MEDIUM`,
      locale: `en-US`,
    });

  const guideVariantListQuery = useGuideVariantListQuery();

  const audienceRoleListQuery = useAudienceRoleListQuery();

  const generateGuideMutation = useGuideGenerateMutation();

  const guideGeneratorInput: Omit<GuideGeneratorInput, `aiApiKey`> = useMemo(
    () => ({
      ...generateOptionsInputState,
      variantIdList: guideVariantListInputState.variantIdList,
    }),
    [generateOptionsInputState, guideVariantListInputState.variantIdList],
  );

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <header className={styles.header}>
          <h1 className={styles.title}>Guide Generator</h1>
          <p className={styles.subtitle}>
            Generate new guides by merging and adapting existing guides.
          </p>
        </header>

        <AuthForm />

        {auth.data && (
          <>
            {audienceRoleListQuery.isLoading && (
              <div className={styles.loading}>
                <div className={styles.spinner} />
                <span>Loading configuration...</span>
              </div>
            )}
            {audienceRoleListQuery.isError && (
              <div className={styles.error}>
                ⚠️ Error loading audience roles:{` `}
                {audienceRoleListQuery.error.message}
              </div>
            )}

            {audienceRoleListQuery.isSuccess && (
              <>
                <GuideVariantListInput
                  disabled={generateGuideMutation.isPending}
                  guideVariantListQuery={guideVariantListQuery}
                  setState={setGuideVariantListInputState}
                  state={guideVariantListInputState}
                />
                <GenerateOptionsInput
                  audienceRoleListQuery={audienceRoleListQuery}
                  disabled={generateGuideMutation.isPending}
                  setState={setGenerateOptionsInputState}
                  state={generateOptionsInputState}
                />
                <GenerateOutput
                  disabled={
                    generateGuideMutation.isPending ||
                    guideGeneratorInput.variantIdList.length === 0
                  }
                  input={guideGeneratorInput}
                  mutation={generateGuideMutation}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const AuthForm: FC = () => {
  const { apiKey, auth, setApiKey } = useSdk();

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionNumber}>1</div>
        <h2 className={styles.sectionTitle}>Authentication</h2>
      </div>

      {!auth.data && (
        <form className={styles.form}>
          <div className={styles.inputGroup}>
            <label className={styles.label} htmlFor="apiKey">
              Mosaic API Key
            </label>
            <input
              autoComplete="off"
              className={styles.input}
              disabled={auth.isPending}
              id="apiKey"
              onChange={({ target: { value: apiKey } }) => setApiKey(apiKey)}
              placeholder="Enter your API key..."
              type="password"
              value={apiKey}
            />
          </div>
          {auth.isPending && (
            <div className={styles.loadingInline}>
              <div className={`${styles.spinner} ${styles.spinnerSmall}`} />
              <span>Validating...</span>
            </div>
          )}
          {auth.isError && (
            <div className={styles.error}>⚠️ {auth.error.message}</div>
          )}
        </form>
      )}

      {auth.data && (
        <div className={styles.authSuccess}>
          <span className={styles.authSuccessIcon}>✓</span>
          <span className={styles.authSuccessText}>Authenticated as</span>
          <span className={styles.authSuccessEmail}>{auth.data.email}</span>
        </div>
      )}
    </section>
  );
};

const GuideVariantListInput: FC<{
  disabled: boolean;
  guideVariantListQuery: GuideVariantListQuery;
  setState: Dispatch<SetStateAction<GuideVariantListInputState>>;
  state: GuideVariantListInputState;
}> = ({ guideVariantListQuery, setState, state }) => {
  const guideVariantById = useMemo(() => {
    return new Map(
      guideVariantListQuery.data?.map((variant) => [variant.id, variant]),
    );
  }, [guideVariantListQuery.data]);

  const filterGuideVariantList = useCallback(
    (filter: string) => {
      const guideVariantListStateSet = new Set(state.variantIdList);
      const guideVariantList =
        guideVariantListQuery.data?.filter(
          (variant) => !guideVariantListStateSet.has(variant.id),
        ) ?? [];
      if (!filter) {
        return guideVariantList;
      }
      const fuse = new Fuse(guideVariantList, {
        includeScore: true,
        keys: [`title`, `primaryUri`],
        threshold: 0.4,
      });
      return fuse
        .search({
          $and: filter
            .split(/\s+/)
            .map((chunk) => chunk.trim())
            .filter((chunk) => chunk.length > 0)
            .map(
              (chunk): Expression => ({
                $or: [{ title: chunk }, { primaryUri: chunk }],
              }),
            ),
        })
        .map((result) => result.item);
    },
    [state.variantIdList, guideVariantListQuery.data],
  );

  const guideVariantOptionList = useMemo(
    () => filterGuideVariantList(state.filter),
    [state.filter, filterGuideVariantList],
  );

  const addVariant = useCallback(
    (id: string) => {
      setState((state) => ({
        ...state,
        variantIdList: [...state.variantIdList, id],
      }));
    },
    [setState],
  );

  const removeVariant = useCallback(
    (id: string) => {
      setState((state) => ({
        ...state,
        variantIdList: state.variantIdList.filter((prevId) => prevId !== id),
      }));
    },
    [setState],
  );

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionNumber}>2</div>
        <h2 className={styles.sectionTitle}>Select Source Guides</h2>
      </div>

      <div className={styles.variantSearch}>
        <input
          className={styles.searchInput}
          onChange={({ target: { value: filter } }) =>
            setState((prev) => ({ ...prev, filter }))
          }
          placeholder="Search guides by title or URL..."
          type="search"
          value={state.filter}
        />
      </div>

      {guideVariantListQuery.isError && (
        <div className={styles.error}>
          ⚠️ {guideVariantListQuery.error.message}
        </div>
      )}

      {guideVariantListQuery.isLoading && (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <span>Loading guides...</span>
        </div>
      )}

      {guideVariantListQuery.isSuccess && (
        <>
          {/* Mobile: Selected chips shown at top */}
          <div className={styles.selectedChipList}>
            {state.variantIdList.length === 0 ? (
              <span className={styles.selectedChipListEmpty}>
                No guides selected yet
              </span>
            ) : (
              state.variantIdList.map((id) => {
                const guideVariant = guideVariantById.get(id);
                if (!guideVariant) {
                  return null;
                }
                return (
                  <div className={styles.selectedChipItem} key={id}>
                    <span>{guideVariant.title}</span>
                    <button
                      className={styles.selectedChipItemRemove}
                      onClick={() => removeVariant(id)}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className={styles.variantContainer}>
            {/* Available Guides */}
            <div className={styles.variantPanel}>
              <div className={styles.variantPanelHeader}>
                <span>Available</span>
                <span className={styles.variantPanelCount}>
                  {guideVariantOptionList.length}
                </span>
              </div>
              <div className={styles.variantList}>
                {guideVariantOptionList.length === 0 ? (
                  <div className={styles.variantListEmpty}>
                    <span className={styles.variantListEmptyIcon}>📄</span>
                    <span>
                      {state.filter
                        ? `No matches found`
                        : `All guides selected`}
                    </span>
                  </div>
                ) : (
                  guideVariantOptionList.map(({ id, primaryUri, title }) => (
                    <div className={styles.variantItem} key={id}>
                      <div className={styles.variantInfo}>
                        <div className={styles.variantTitle}>{title}</div>
                        {primaryUri && (
                          <div className={styles.variantUri}>
                            <a
                              href={primaryUri}
                              onClick={(e) => e.stopPropagation()}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {primaryUri}
                            </a>
                          </div>
                        )}
                      </div>
                      <button
                        className={`${styles.variantAction} ${styles.variantActionAdd}`}
                        onClick={() => addVariant(id)}
                        type="button"
                      >
                        + Add
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Selected Guides (hidden on mobile, shown as chips above) */}
            <div
              className={`${styles.variantPanel} ${styles.variantPanelSelected}`}
            >
              <div className={styles.variantPanelHeader}>
                <span>Selected</span>
                <span
                  className={`${styles.variantPanelCount} ${state.variantIdList.length > 0 ? styles.variantPanelCountSelected : ``}`}
                >
                  {state.variantIdList.length}
                </span>
              </div>
              <div className={styles.variantList}>
                {state.variantIdList.length === 0 ? (
                  <div className={styles.variantListEmpty}>
                    <span className={styles.variantListEmptyIcon}>📋</span>
                    <span>Select guides to merge</span>
                  </div>
                ) : (
                  state.variantIdList.map((id) => {
                    const guideVariant = guideVariantById.get(id);
                    if (!guideVariant) {
                      return null;
                    }
                    return (
                      <div
                        className={`${styles.variantItem} ${styles.variantItemSelected}`}
                        key={id}
                      >
                        <div className={styles.variantInfo}>
                          <div className={styles.variantTitle}>
                            {guideVariant.title}
                          </div>
                          {guideVariant.primaryUri && (
                            <div className={styles.variantUri}>
                              {guideVariant.primaryUri}
                            </div>
                          )}
                        </div>
                        <button
                          className={`${styles.variantAction} ${styles.variantActionRemove}`}
                          onClick={() => removeVariant(id)}
                          type="button"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

const GenerateOptionsInput: FC<{
  audienceRoleListQuery: AudienceRoleListQuery;
  disabled: boolean;
  setState: Dispatch<SetStateAction<GenerateOptionsInputState>>;
  state: GenerateOptionsInputState;
}> = ({ audienceRoleListQuery, disabled, setState, state }) => {
  const onAiProviderChange = useCallback(
    (provider: AiProvider) => {
      setState((state) => {
        if (state.ai.provider === provider) {
          return state;
        }
        return {
          ...state,
          ai: { ...state.ai, model: undefined, provider },
        };
      });
    },
    [setState],
  );

  const onAiModelChange = useCallback(
    (model: string) => {
      setState(({ ai: { model: _model, ...ai }, ...state }) => ({
        ...state,
        ai: GuideGeneratorInput.shape.ai.parse({
          ...ai,
          model: model.length > 0 ? model : undefined,
        }),
      }));
    },
    [setState],
  );

  const onLocaleChange = useCallback(
    (locale: Locale) => {
      setState((state) => ({ ...state, locale }));
    },
    [setState],
  );

  const onFluencyChange = useCallback(
    (fluency: string) => {
      setState((state) => ({
        ...state,
        fluency: fluency.length > 0 ? Fluency.parse(fluency) : undefined,
      }));
    },
    [setState],
  );

  const onLengthChange = useCallback(
    (length: GuideLength) => {
      setState((state) => ({ ...state, length }));
    },
    [setState],
  );

  const onAudienceRoleChange = useCallback(
    (audienceRoleId: string | undefined) => {
      setState(({ audienceRoleId: _audienceRoleId, ...state }) => ({
        ...state,
        audienceRoleId,
      }));
    },
    [setState],
  );

  const onEffortChange = useCallback(
    (effort: string) => {
      setState((state) => ({
        ...state,
        ai: {
          ...state.ai,
          effort: effort.length > 0 ? AiEffort.parse(effort) : undefined,
        },
      }));
    },
    [setState],
  );

  const onTemperatureChange = useCallback(
    (temperature: string) => {
      setState((state) => ({
        ...state,
        ai: {
          ...state.ai,
          temperature:
            temperature.length > 0 ? Number.parseFloat(temperature) : undefined,
        },
      }));
    },
    [setState],
  );

  const onSeedChange = useCallback(
    (seed: string) => {
      setState((state) => ({
        ...state,
        ai: {
          ...state.ai,
          seed: seed.length > 0 ? Number.parseInt(seed, 10) : undefined,
        },
      }));
    },
    [setState],
  );

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionNumber}>3</div>
        <h2 className={styles.sectionTitle}>Generation Options</h2>
      </div>

      <div className={styles.optionsGrid}>
        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="provider">
            AI Provider
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="provider"
            onChange={({ target: { value: provider } }) =>
              onAiProviderChange(AiProvider.parse(provider))
            }
            value={state.ai.provider}
          >
            {AiProvider.options.map((provider) => (
              <option key={provider} value={provider}>
                {provider.charAt(0).toUpperCase() + provider.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="model">
            Model
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="model"
            onChange={(e) => onAiModelChange(e.target.value)}
            value={state.ai.model ?? ``}
          >
            {modelMap[state.ai.provider].map(({ label, value: model }) => (
              <option key={model} value={model}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="locale">
            Target Language
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="locale"
            onChange={({ target: { value: locale } }) =>
              onLocaleChange(Locale.parse(locale))
            }
            value={state.locale}
          >
            {Locale.options.map((locale) => (
              <option key={locale} value={locale}>
                {localeLabelMap[locale]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="fluency">
            Fluency Level
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="fluency"
            onChange={({ target: { value: fluency } }) =>
              onFluencyChange(fluency)
            }
            value={state.fluency ?? ``}
          >
            <option value="">Any level</option>
            {Fluency.options.map((fluency) => (
              <option key={fluency} value={fluency}>
                {fluencyLabelMap[fluency]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="length">
            Target Length
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="length"
            onChange={({ target: { value: length } }) =>
              onLengthChange(GuideLength.parse(length))
            }
            value={state.length}
          >
            {GuideLength.options.map((length) => (
              <option key={length} value={length}>
                {guideLengthLabelMap[length]}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="audienceRoleId">
            Audience Role
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="audienceRoleId"
            onChange={({ target: { value: audienceRoleId } }) =>
              onAudienceRoleChange(
                audienceRoleId.length > 0 ? audienceRoleId : undefined,
              )
            }
            value={state.audienceRoleId ?? ``}
          >
            <option value="">Any audience</option>
            {audienceRoleListQuery.data?.map(({ id, slug }) => (
              <option key={id} value={id}>
                {slug.replaceAll(`-`, ` `)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="effort">
            Thinking Effort
          </label>
          <select
            className={styles.select}
            disabled={disabled}
            id="effort"
            onChange={({ target: { value: effort } }) => onEffortChange(effort)}
            value={state.ai.effort ?? ``}
          >
            <option value="">Default</option>
            {AiEffort.options.map((effort) => (
              <option key={effort} value={effort}>
                {effort.charAt(0).toUpperCase() + effort.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="temperature">
            Temperature
          </label>
          <input
            className={styles.input}
            disabled={disabled}
            id="temperature"
            max={2}
            min={0}
            onChange={({ target: { value: temperature } }) =>
              onTemperatureChange(temperature)
            }
            placeholder="Default"
            step={0.1}
            type="number"
            value={state.ai.temperature ?? ``}
          />
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label} htmlFor="seed">
            Seed
          </label>
          <input
            className={styles.input}
            disabled={disabled}
            id="seed"
            onChange={({ target: { value: seed } }) => onSeedChange(seed)}
            placeholder="Random"
            type="number"
            value={state.ai.seed ?? ``}
          />
        </div>
      </div>
    </section>
  );
};

const GenerateOutput: FC<{
  disabled: boolean;
  input: Omit<GuideGeneratorInput, `aiApiKey`>;
  mutation: GuideGenerateMutation;
}> = ({ disabled, input, mutation }) => {
  const { data: output } = mutation;

  /**
   * Download the generated guide as Markdown.
   */
  const handleDownload = useCallback(() => {
    if (!output) {
      return;
    }

    const { contentMarkdown, summaryMarkdown, title } = output;
    const markdown = dedent`
      # ${title}
      ${summaryMarkdown}
      ---
      ${contentMarkdown}
    `;
    const blob = new Blob([markdown], { type: `text/markdown` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement(`a`);
    link.href = url;
    link.download = `${title.replaceAll(/[^\da-z]/gi, `-`).toLowerCase()}.md`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [output]);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionNumber}>4</div>
        <h2 className={styles.sectionTitle}>Generate</h2>
      </div>

      <button
        className={`${styles.button} ${styles.generateButton}`}
        disabled={disabled}
        onClick={() => mutation.mutate(input)}
        type="button"
      >
        <span className={styles.generateButtonContent}>
          {mutation.isPending ? (
            <>
              <div className={`${styles.spinner} ${styles.spinnerSmall}`} />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <span>✨</span>
              <span>Generate Guide</span>
            </>
          )}
        </span>
      </button>

      {mutation.isError && (
        <div className={styles.error}>
          ⚠️ Error generating: {mutation.error.message}
        </div>
      )}

      {mutation.isSuccess && output && (
        <div className={styles.result}>
          <div className={styles.resultHeader}>
            <h3 className={styles.resultTitle}>{output.title}</h3>
            <div className={styles.resultActionList}>
              <button
                className={`${styles.button} ${styles.buttonSecondary} ${styles.buttonSmall}`}
                onClick={() => mutation.reset()}
                type="button"
              >
                Clear
              </button>
              <button
                className={`${styles.button} ${styles.buttonSuccess} ${styles.buttonSmall}`}
                onClick={handleDownload}
                type="button"
              >
                ↓ Download
              </button>
            </div>
          </div>

          <div className={styles.resultSection}>
            <div className={styles.resultSectionTitle}>Summary</div>
            <div className={styles.resultSummary}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {output.summaryMarkdown}
              </ReactMarkdown>
            </div>
          </div>

          <div className={styles.resultSection}>
            <div className={styles.resultSectionTitle}>Full Guide</div>
            <div className={styles.resultContent}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {[output.contentMarkdown, output.referenceListMarkdown].join(
                  `\n\n`,
                )}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Page;
